import { Info } from "lucide-react";
import { format } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BundleUsage } from "@/pages/users/types/user";

interface SessionsSummaryProps {
  sessionsSummary: {
    total: number;
    used: number;
  };
  sessionsRemaining: number;
  bundlesGroupedByName: Record<string, BundleUsage[]>;
  closestToExpiry?: {
    remaining: number;
    daysUntilExpiry: number;
  } | null;
}

export function SessionsSummary({
  sessionsSummary,
  sessionsRemaining,
  bundlesGroupedByName,
  closestToExpiry,
}: SessionsSummaryProps) {
  return (
    <div>
      <div className="flex items-center justify-between gap-1.5 mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-white/90">Sessions Utilised</span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-white/60 hover:text-white/90 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs">
                <div className="space-y-3">
                  <p className="font-semibold text-xs">Sessions by Bundle:</p>
                  {Object.entries(bundlesGroupedByName).map(([bundleName, bundles]) => (
                    <div key={bundleName} className="space-y-1.5">
                      <div className="font-medium text-xs">{bundleName}</div>
                      {bundles.map((bundle) => (
                        <div key={bundle.id} className="text-xs text-muted-foreground pl-2">
                          <div>Bought on {format(new Date(bundle.start_date), "MMM dd, yyyy")}</div>
                          <div>
                            {bundle.utilized_session_quantity} / {bundle.total_session_quantity} used
                            {' • '}
                            {bundle.total_session_quantity - bundle.utilized_session_quantity} remaining
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {Object.keys(bundlesGroupedByName).length === 0 && (
                    <p className="text-xs text-muted-foreground">No bundles found</p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="text-sm font-semibold text-white/90">
          {sessionsSummary.used} / {sessionsSummary.total}
        </div>
      </div>
      <ul className="list-disc list-inside pl-4">
        <li className="text-sm font-medium text-white/90">
          {sessionsRemaining} {sessionsRemaining === 1 ? 'session' : 'sessions'} remaining
        </li>
        {closestToExpiry && closestToExpiry.daysUntilExpiry <= 7 && closestToExpiry.remaining > 0 && closestToExpiry.daysUntilExpiry > 0 && (
          <li className="text-sm font-medium text-white/90">
            {closestToExpiry.remaining} {closestToExpiry.remaining === 1 ? 'bundle' : 'bundles'} will expire in {closestToExpiry.daysUntilExpiry} {closestToExpiry.daysUntilExpiry === 1 ? 'day' : 'days'}
          </li>
        )}
      </ul>
    </div>
  );
}

